import EmailAddressSchema from "_shared/email-addresses/email-address-schema";

/**
 * What one column holds, which decides how the row editor renders it and how a parsed cell is
 * read back.
 */
export type BulkInvitationFieldKind = "text" | "email" | "boolean" | "select";

/** One choice offered by a `select` field. */
export type BulkInvitationFieldOption = {
  readonly value: string;
  readonly label: string;
};

/** A value a row can carry. Cells are text or ticks; nothing here is numeric. */
export type BulkInvitationCellValue = string | boolean | undefined;

/** One entered row, keyed by field. Rows are plain data so a parsed sheet and a typed row are the same thing. */
export type BulkInvitationRowValues = Record<string, BulkInvitationCellValue>;

/** A reason one field of one row cannot be submitted, in the words of the person entering it. */
export type BulkInvitationRowError = {
  readonly key: string;
  readonly message: string;
};

export type BulkInvitationFieldSpec = {
  /** Stable key, and the key this field occupies on a row. */
  readonly key: string;

  /**
   * The column heading. One string serves the grid and the spreadsheet template, because two
   * spellings of one heading is how a template stops matching the grid it was generated from.
   */
  readonly header: string;

  readonly kind: BulkInvitationFieldKind;

  /** Whether a row is refused without it. */
  readonly required: boolean;

  /** Why the column exists, shown under the input in the row editor and never in the sheet. */
  readonly hint?: string;

  /**
   * What the template's example row puts in this column, written as somebody would type it - a
   * tick column says "Yes". Every column the sheet carries has one, so the example row shows a
   * complete entry rather than a partial one somebody copies the gaps of.
   */
  readonly example?: string;

  /** The choices a `select` offers. Resolved when the spec is built, because they are loaded. */
  readonly options?: readonly BulkInvitationFieldOption[];

  /**
   * Keeps the column out of the spreadsheet template and out of parsing, while leaving it in the
   * grid. For a field whose value is an id nobody can type: a person filling a sheet in knows
   * their colleague's title, and does not know a permission template's GUID.
   */
  readonly omitFromSpreadsheet?: boolean;

  /**
   * A rule beyond required-ness and kind. Returns the message to show, or null when the value is
   * acceptable. Receives the whole row, so a rule may read a sibling field.
   */
  readonly validate?: (value: BulkInvitationCellValue, row: BulkInvitationRowValues) => string | null;
};

/**
 * One column of the grid, which may show several fields read together - a person's name, a firm's
 * address - under one heading.
 *
 * The grid is where a roster is reviewed rather than typed: each row is edited in a form of its
 * own, field by field, and the spreadsheet keeps one column per field. So the grid is free to
 * group what is read together, and has to, to fit beside the navigation. With one 180px column per
 * field the team leader roster was 2,288px wide in a 1,046px pane at a 1366 window (#6530).
 */
export type BulkInvitationGridColumn = {
  readonly id: string;
  readonly header: string;

  /** The fields shown, in order. Empty ones are left out rather than leaving a stray separator. */
  readonly keys: readonly string[];

  /** Between the fields' values. A space by default, which is right for a name. */
  readonly separator?: string;

  /**
   * The narrowest the column may get, in pixels: Material React Table makes a column's size its
   * minimum width, and the sum of them is the narrowest the table can be. Text wraps inside it.
   */
  readonly size: number;

  /** Kept in view while the other columns scroll sideways: the ones that say whose row it is. */
  readonly pinned?: boolean;

  /** Cuts free text off after this many lines. The row editor shows all of it. */
  readonly maxLines?: number;
};

export type BulkInvitationColumnSpec = {
  /** Distinguishes the two rosters in test names, storage keys and the template file name. */
  readonly id: string;

  /** What the person entering the roster is listing, used in prose the grid renders. */
  readonly noun: string;

  readonly templateFileName: string;
  readonly templateSheetName: string;

  readonly fields: readonly BulkInvitationFieldSpec[];

  /** How the grid shows the fields. Every field appears in exactly one of these. */
  readonly gridColumns: readonly BulkInvitationGridColumn[];

  /**
   * A rule no single field owns - the firm's address, where any one part will do and all of them
   * empty will not. Returns one error per field it wants marked.
   */
  readonly validateRow?: (row: BulkInvitationRowValues) => readonly BulkInvitationRowError[];
};

/** The field whose value identifies a person, which is what duplicate detection compares. */
export const BULK_INVITATION_EMAIL_KEY = "email";

export function createEmptyRow(spec: BulkInvitationColumnSpec): BulkInvitationRowValues {
  const row: BulkInvitationRowValues = {};
  for (const field of spec.fields) {
    row[field.key] = field.kind === "boolean" ? false : "";
  }
  return row;
}

/** The columns the template carries and a parsed sheet is read against. */
export function spreadsheetFields(spec: BulkInvitationColumnSpec): readonly BulkInvitationFieldSpec[] {
  return spec.fields.filter((field) => !field.omitFromSpreadsheet);
}

export function readText(row: BulkInvitationRowValues, key: string): string {
  const value = row[key];
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Everything wrong with one row that can be known without asking the server.
 *
 * Deliberately only three kinds of thing: a missing required value, a value of the wrong shape,
 * and the same person entered twice. Whether the firm accepts that email domain, and whether a
 * permission template belongs to the caller, are the server's answers - it owns both, and a copy
 * of either rule here would be a second place for them to be wrong. Those come back per row after
 * submit instead.
 *
 * `precedingRows` is the rows above this one rather than all of them, so a duplicate is reported
 * on the second occurrence and the first stays clean: the person entering it keeps one of the two
 * and fixes the other, which is what they meant to have.
 */
export function validateBulkInvitationRow(
  spec: BulkInvitationColumnSpec,
  row: BulkInvitationRowValues,
  precedingRows: readonly BulkInvitationRowValues[] = [],
): readonly BulkInvitationRowError[] {
  const errors: BulkInvitationRowError[] = [];

  for (const field of spec.fields) {
    const raw = row[field.key];
    const text = typeof raw === "string" ? raw.trim() : "";

    if (field.required && field.kind !== "boolean" && text.length === 0) {
      errors.push({ key: field.key, message: `${field.header} is required.` });
      continue;
    }

    if (field.kind === "email" && text.length > 0 && !EmailAddressSchema.isValid(text)) {
      errors.push({ key: field.key, message: "This is not a valid email address." });
      continue;
    }

    const message = field.validate?.(raw, row);
    if (message) errors.push({ key: field.key, message });
  }

  const email = readText(row, BULK_INVITATION_EMAIL_KEY).toLowerCase();
  const alreadyEntered =
    email.length > 0 &&
    precedingRows.some((earlier) => readText(earlier, BULK_INVITATION_EMAIL_KEY).toLowerCase() === email);
  if (alreadyEntered) {
    errors.push({
      key: BULK_INVITATION_EMAIL_KEY,
      message: "This address appears earlier in the list.",
    });
  }

  const rowErrors = spec.validateRow?.(row) ?? [];
  for (const rowError of rowErrors) errors.push(rowError);

  return errors;
}

/** Every row's errors, keyed by the row's position, so the grid can mark rows without re-deriving. */
export function validateBulkInvitationRows(
  spec: BulkInvitationColumnSpec,
  rows: readonly BulkInvitationRowValues[],
): ReadonlyMap<number, readonly BulkInvitationRowError[]> {
  const byIndex = new Map<number, readonly BulkInvitationRowError[]>();
  for (let index = 0; index < rows.length; index++) {
    const errors = validateBulkInvitationRow(spec, rows[index], rows.slice(0, index));
    if (errors.length > 0) byIndex.set(index, errors);
  }
  return byIndex;
}
